import { BeamstarTurnOutcome, StatusVariant } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';

// en-US because newer ICU data writes en-GB millions with a lowercase m.
const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCount = (value: number) => value.toLocaleString('en-GB');

export const formatTokens = (value: number) => compactNumber.format(value);

export const counted = (count: number, singular: string, plural: string) =>
  `${formatCount(count)} ${count === 1 ? singular : plural}`;

export const formatDateTime = (timestamp: string) =>
  new Date(timestamp).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

export const formatTime = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** A day of the summary, which the building agent counts in UTC. */
export const formatDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

export const formatDuration = (milliseconds: number) => {
  if (milliseconds < 10_000) {
    return `${(milliseconds / 1000).toFixed(1)} s`;
  }
  const seconds = Math.round(milliseconds / 1000);
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
};

/** The building agent records workflows by id, so this is the id in words. */
export const workflowName = (workflowId: string) => formatEnumToReadableText(workflowId);

export const turnOutcomeMapping: Record<
  BeamstarTurnOutcome,
  { text: string; variant: StatusVariant }
> = {
  [BeamstarTurnOutcome.DONE]: { text: 'Finished', variant: StatusVariant.SUCCESS },
  [BeamstarTurnOutcome.ERROR]: { text: 'Failed', variant: StatusVariant.ERROR },
  [BeamstarTurnOutcome.NEVER_FINISHED]: { text: 'Never finished', variant: StatusVariant.ERROR },
  [BeamstarTurnOutcome.CONNECTION_LOST]: {
    text: 'Connection lost',
    variant: StatusVariant.WARNING,
  },
  [BeamstarTurnOutcome.STOPPED]: { text: 'Stopped', variant: StatusVariant.WARNING },
  [BeamstarTurnOutcome.RUNNING]: { text: 'Running', variant: StatusVariant.INFO },
};
